import React, { memo, useState, MouseEventHandler, useEffect } from "react";
import cn from "classnames";
import styles from "./MinersTab.module.scss";
import istyles from "./index.module.scss";
import { Tooltip } from "antd";
import { useIntl } from "react-intl";
import { ReactComponent as Edit } from "@assets/redesign/icons/inputEdit.svg";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@store";
import { Tab, tabIDToType } from "@store/minersSelect/actionTypes";
import { setDragOverTab, setTab } from "@store/minersSelect/actions";
import { moveMinerToSpecialTabRequest } from "@services/changeMinerSettings";
import useTypedRequest from "@utils/hooks/useTypedRequest";
import { moveMinerToUserTabRequest } from "@services/vtabs";
import { DefaultDragSources, DragSources, DragTargets, DragType } from ".";
import { chooseWithModal } from "@components/modals/newModals/ChoiceModal/ChoiceModal";
import dbg from "@utils/dbg";
import { sleep } from "@utils/sleep";
import { useMe } from "@utils/hooks/useMe";

export interface ITabProps {
  readonly thisID: Tab;
  readonly currentID: number;

  readonly tabName?: string;
  readonly hasItems?: boolean;
  readonly title?: string;
  readonly children?: React.ReactNode;
  readonly special?: boolean;
  readonly last?: boolean;
  readonly skippable?: boolean;

  setTab(id: number): void;
  deleteTab?(id: number): void;
  renameTab?(id: number, newName: string): void;
  onClick?: MouseEventHandler<HTMLDivElement>;
}

const separateTabs: ReadonlySet<Tab> = new Set([Tab.INCORRECTID, Tab.UNKNOWN]);

const useControl = (props: ITabProps) => {
  const [titleIsEditable, setTitleEditable] = useState(false);
  const isDragOver = useSelector(
    (state: RootState) => state.minersSelect.dragOverTabID !== undefined
  );
  const [noDrop, setNoDrop] = useState(false);
  const dragSource = useSelector(
    (state: RootState) => state.minersSelect.dragFromTab
  );
  const { request: moveMinerToUserTab } = useTypedRequest(
    moveMinerToUserTabRequest
  );
  const { request: moveMinerToSpecialTab } = useTypedRequest(
    moveMinerToSpecialTabRequest
  );
  const me = useMe();
  const isAdmin = me?.is_admin;

  const dispatch = useDispatch();

  const dragged = useSelector(
    (root: RootState) => root.minersSelect.dragMinersIDs
  );
  const isDrag = useSelector(
    (root: RootState) => !!root.minersSelect.dragMinersIDs?.length
  );
  const total = useSelector((root: RootState): number => {
    const name = tabIDToType(props.thisID);
    if (name === undefined) return 0;
    return root.storedMiners.TabsTotals[name] ?? 0;
  });

  //const allTotals = useSelector((root: RootState) => root.storedMiners.TabsTotals);
  //useEffect(() => dbg.log("TOTALS", allTotals), [allTotals]);

  return {
    total,
    noDrop,
    titleIsEditable,
    isDrag,
    setTitleEditable,
    finishEditing(newName?: string) {
      setTitleEditable(false);
      if (newName) {
        props.renameTab && props.renameTab(props.thisID, newName);
      }
    },
    onMouseEnter() {
      if (isDrag) {
        dispatch(setDragOverTab(props.thisID));
        if (!isAdmin) {
          setNoDrop(true);
          return;
        }
        let noDrop = props.thisID === dragSource;
        if ((dragSource ?? 0) > 0) {
          //dbg.log("NODROP/1");
          noDrop ||= props.thisID < 0;
        } else {
          //dbg.log("NODROP/2", {noDrop, "props.thisID > 0": props.thisID > 0});
          noDrop ||=
            props.thisID < 0 &&
            (props.thisID === Tab.UNKNOWN ||
              dragSource === undefined ||
              !DragSources.has(dragSource) ||
              !Object.hasOwn(DragTargets, props.thisID) ||
              !Object.hasOwn(DragTargets[props.thisID], dragSource));
        }

        //dbg.log("NODROP", noDrop, props.thisID === dragSource);
        if (noDrop) setNoDrop(true);
      }
    },
    onMouseLeave() {
      dispatch(setDragOverTab());
      setNoDrop(false);
    },
    async onMouseUp() {
      if (
        !isDragOver ||
        !dragged ||
        noDrop ||
        !isDrag ||
        props.currentID === props.thisID ||
        dragSource === undefined
      )
        return;
      let sourcesTab = DefaultDragSources;
      if (props.thisID < 0) {
        const st = DragTargets[props.thisID];
        if (st) sourcesTab = st;
      }

      let dragType =
        dragSource > 0 && props.thisID > 0
          ? DragType.USER
          : dragSource < 0 && props.thisID < 0
          ? DragType.MOVECALL
          : dragSource < 0 && props.thisID > 0
          ? DragType.COPY
          : sourcesTab[dragSource];

      if (!dragType) return;

      if (dragType === DragType.USER) {
        const pos = await chooseWithModal(
          dispatch,
          "machines.table.tabs.select_action",
          ["machines.table.tabs.move", "machines.table.tabs.copy"]
        );
        if (pos < 0) return;
        dragType = [DragType.MOVE, DragType.COPY][pos];
      }

      if (dragType === DragType.MOVE) {
        if (dragSource > 0) {
          await moveMinerToUserTab(dragSource, dragged, false);
        } else {
          await moveMinerToSpecialTab(dragSource, dragged, false);
        }
      }

      if (props.thisID > 0) {
        await moveMinerToUserTab(props.thisID, dragged, true);
      } else {
        await moveMinerToSpecialTab(props.thisID, dragged, true);
      }

      await sleep(1000);
      dispatch(setTab(props.thisID));
    },

    onClick(e: React.MouseEvent<HTMLDivElement>) {
      (props.onClick ?? ((e) => props.setTab(props.thisID)))(e);
    },
  };
};

function MinersTab(props: ITabProps) {
  const ctrl = useControl(props);
  const { formatMessage } = useIntl();

  if (props.thisID === Tab.RACKS) {
    console.log("total", ctrl.total);
  }

  return (
    <div
      className={cn(styles.tabs_btn, {
        [styles.active]: props.currentID === props.thisID,
        [styles.noDrop]: ctrl.noDrop,
        [istyles.noDrag]: !ctrl.isDrag,
        [styles.last]: !!props.last,
        [styles.special]: !!props.special,
        [styles.skippable]: !!props.skippable,
        [styles.separate]: separateTabs.has(props.thisID),
        [styles.firstSeparate]: props.thisID === Tab.INCORRECTID,
        [styles.user]: props.thisID > 0,
      })}
      onClick={ctrl.onClick}
      title={props.title || props.tabName}
      onMouseEnter={ctrl.onMouseEnter}
      onMouseLeave={ctrl.onMouseLeave}
      onMouseUp={ctrl.onMouseUp}
    >
      {props.children ? (
        props.children
      ) : props.renameTab ? (
        <div className={cn(styles.title, styles.editable)}>
          {ctrl.titleIsEditable ? (
            <input
              className={styles.editableTitle}
              autoFocus
              defaultValue={props.tabName}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  ctrl.finishEditing((e.target as HTMLInputElement).value);
                }
                if (e.key === "Escape") {
                  ctrl.finishEditing();
                }
              }}
            />
          ) : (
            <>
              <div className={styles.tooltipContainer}>
                <Tooltip
                  trigger={["click", "hover"]}
                  destroyTooltipOnHide
                  placement="top"
                  overlayInnerStyle={{
                    backgroundColor: "#fff",
                  }}
                  color="#fff"
                  title={
                    <div className={styles.tooltip_wrapper}>
                      <div
                        className={cn(styles.handle, {
                          [styles.inactive]: props.hasItems,
                        })}
                        onClick={() =>
                          props.deleteTab && props.deleteTab(props.thisID)
                        }
                      >
                        {formatMessage({ id: "machines.table.tabs.delete" })}
                      </div>
                      <div
                        className={cn(styles.handle, {
                          [styles.inactive]: !props.renameTab,
                        })}
                        onClick={() => ctrl.setTitleEditable(true)}
                      >
                        {formatMessage({ id: "machines.table.tabs.rename" })}
                      </div>
                    </div>
                  }
                >
                  <Edit className={styles.edit} />
                </Tooltip>
              </div>
              <span className={styles.titleText}>{props.tabName}</span>
            </>
          )}
          <div
            className={cn(styles.totalLabel, {
              [styles.show]: true,
              [styles.active]: props.currentID === props.thisID,
            })}
          >
            {ctrl.total}
          </div>
        </div>
      ) : (
        <div
          className={styles.title_special}
          title={props.title || props.tabName}
        >
          <div className={styles.titleText}>{props.tabName}</div>
          <div
            className={cn(styles.totalLabel, {
              [styles.show]: true,
              [styles.active]: props.currentID === props.thisID,
            })}
          >
            {ctrl.total}
          </div>
        </div>
      )}
    </div>
  );
}

export default memo(MinersTab);
